import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Doc } from '../../../../../../../types';
import {
  updateTrashedDocsStatus,
  UserKeys,
} from '../../../../../../api/users-api';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import GeneralDialog from '../../../../../../shared/general-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import ActingAsUserMessage from '../../../../../../shared/acting-as-user-message';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DriveTrashDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  selectedFolder: string;
  changeLogRoles: string[];
}

export default function DriveTrashDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  selectedFolder,
  changeLogRoles,
}: DriveTrashDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateTrashedDocsStatus(userEmail, selectedDocs, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The documents or folders were moved to trash successfully!',
      });

      // update all folders
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveFolders, userEmail],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveFolders, userEmail, selectedFolder],
      });

      // update docs of current folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedDriveDocuments, userEmail, selectedFolder],
      });

      // update docs of trash folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, userEmail],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue moving the selected documents or folders to trash. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.content.trash_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .trash_drive_content,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .trash_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanTrashDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Move to Trash"
      dialogContent={
        <Box sx={{ px: 4 }}>
          <ActingAsUserMessage userEmail={userEmail} />
          <Typography>
            {selectedDocs.length === 1
              ? `Do you wish to move the selected ${
                  selectedDocs[0].mimeType.includes('folder')
                    ? 'folder'
                    : 'document'
                }: "${selectedDocs[0].title}" to the trash?`
              : `Do you wish to move the ${selectedDocs.length} selected documents or folders to the trash?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Trash',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
